/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';

import { Grid, Hidden, PageGridContainer, VisuallyHidden, breakpoints } from '@coursera/cds-core';

import AdditionalInfoResponsiveComponent from 'bundles/account-profile/components/recruiting-profile-viewer/AdditionalInfoResponsiveComponent';
import { LearnerProfileNotFoundView } from 'bundles/account-profile/components/recruiting-profile-viewer/NotFoundViews';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import AdditionalInfoSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/AdditionalInfoSection';
import EducationSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/education/EducationSection';
import ExperienceSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/experience/ExperienceSection';
import HighlightsSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/highlights/HighlightsSection';
import SkillsSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/iMocha-skills/SkillsSection';
import PrivacyNotification from 'bundles/account-profile/components/recruiting-profile-viewer/components/privacy-settings/PrivacyNotification';
import PrivacySettingsSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/privacy-settings/PrivacySettingsSection';
import InviteToApply from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/InviteToApply';
import InviteToApplyBanner from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/InviteToApplyBanner';
import PhotoAndInvitationSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/PhotoAndInvitationSection';
import WorkEligibilitySection from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-eligibility/WorkEligibilitySection';
import WorkPreferencesSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-preferences/WorkPreferencesSection';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import _t from 'i18n!nls/account-profile';

const E2E_SELECTORS = {
  DESKTOP_PANEL: 'profile-desktop-panel',
} as const;

const styles = {
  container: () => css`
    padding: var(--cds-spacing-600);
    background-color: var(--cds-color-grey-25);
    width: 100%;

    ${breakpoints.between('xs', 'sm')} {
      padding-left: var(--cds-spacing-300);
      padding-right: var(--cds-spacing-300);
    }

    ${breakpoints.down('xs')} {
      padding-left: 0;
      padding-right: 0;
    }
  `,
  pageGridContainer: () => css`
    ${breakpoints.down('xs')} {
      padding-left: 0;
      padding-right: 0;
    }
  `,
};

export const RecruitingProfileViewer = () => {
  const {
    profileState = {},
    isProfileLoading,
    editable,
    isRecruiter,
    learnerProfileFailedToLoad,
  } = useContext(ProfileContext);

  const shouldDisplayNotFoundView = () => {
    if (learnerProfileFailedToLoad) {
      return true;
    } else if (isProfileLoading || !profileState) {
      return false;
      // Catches the cases for if a profile is not public and if the user is a recruiter who is viewing a profile that isn't included in talent pipeline
    } else if (!editable && !(profileState?.showToPublic || (isRecruiter && profileState?.includedInTalentPipeline))) {
      return true;
    } else {
      return false;
    }
  };

  if (shouldDisplayNotFoundView()) {
    return <LearnerProfileNotFoundView />;
  }

  return (
    <main css={styles.container} data-testid="learner-skills-profile">
      <CourseraMetatags metaNameAndProperties={{ title: `${profileState?.fullName}`, disableCrawlerIndexing: true }} />
      <VisuallyHidden component="h1" className="rc-A11yScreenReaderOnly">
        {_t(`${profileState?.fullName} account profile`)}
      </VisuallyHidden>
      <InviteToApplyBanner />
      <PrivacyNotification />
      <InviteToApply />
      <PageGridContainer css={styles.pageGridContainer} maxWidth="lg">
        <Grid item sm={12} md={4} lg={3}>
          <PhotoAndInvitationSection />
          <Hidden mdUp>
            <AdditionalInfoResponsiveComponent />
          </Hidden>
          <Hidden smDown>
            <span data-e2e={E2E_SELECTORS.DESKTOP_PANEL}>
              <HighlightsSection />
              <WorkPreferencesSection />
              <WorkEligibilitySection />
              <AdditionalInfoSection />
            </span>
          </Hidden>
        </Grid>
        <Grid item sm={12} md={8} lg={9}>
          <SkillsSection />
          <ExperienceSection />
          <EducationSection />
          <PrivacySettingsSection />
        </Grid>
      </PageGridContainer>
    </main>
  );
};

export default RecruitingProfileViewer;
